import * as React from 'react';
import { capitalize } from 'lodash';
import pluralize from 'pluralize';

import Avatar from 'components/Avatar';
import Glyph from 'components/Glyph';
import GroupRecipientAvatar from 'chat_v2/components/GroupRecipientAvatar';
// @ts-ignore
import GroupChatAvatar from 'chat_v2/components/GroupChatAvatar';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import OrgAvatar from 'organizations/components/OrgAvatar';
import Text from 'components/Text';
import Tooltip from 'components/Tooltip';
import IndividualRecipientAvatar from 'chat_v2/components/IndividualRecipientAvatar';
import errorLogger from 'utils/errorLogger';
import { ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity } from 'chat_v2/components/SplitComposer/queries/__generated__/ComposerRecipients';

const TRUNCATED_NAME_SIZE = 40;

const getDisplayProps = (entity: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity) => {
  switch (entity.recipient.type) {
    case 'individual':
      return {
        name: entity.content.title,
        avatar: (
          <IndividualRecipientAvatar entity={entity} size="xsmall" color="whiteout" hideBadge />
        ),
      };
    case 'group':
      return {
        name: entity.content.title,
        avatar: (
          <GroupRecipientAvatar entity={entity} size="xsmall" />
        )
      };
    case 'district':
      return {
        name: entity.content.title,
        avatar: <OrgAvatar size="xsmall" />,
      }
    case 'group_chat':
      return {
        name: entity.content.title,
        avatar: <GroupChatAvatar size="xsmall" />,
      }
    case 'student_id_list':
      return {
        name: entity.content.title,
        avatar: <Avatar color="whiteout" glyphName="spreadsheet" size="xsmall" />,
      }
    default:
      return null;
  }
};

function Token({ entity, selectedRoles }: { entity: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity, selectedRoles?: string[] }) {
  const displayProps = getDisplayProps(entity);
  if (!displayProps) {
    errorLogger(`Could not render token for recipient: invalid type ${entity.recipient.type}`);
    return null;
  }

  const recipientId = entity.recipient.uuid;
  const { name, avatar } = displayProps;

  let displayName = name;
  if (name && name.length > TRUNCATED_NAME_SIZE) {
    // this is the officially character code for the ellipses. And the official
    // way to get it to display properly with the Text component. How fun.
    displayName = name.substr(0, TRUNCATED_NAME_SIZE) + String.fromCharCode(8230);
  }

  const selectedRolesText = selectedRoles ? selectedRoles.map(role => capitalize(pluralize(role, 2))).join(', ') : undefined;

  const tooltip = (
    <Tooltip>
      {name}
      {selectedRolesText ? (
        <Text size="xxxsmall" color="ruler" element="div">
          {selectedRolesText}
        </Text>
      ) : null}
    </Tooltip>
  );
  return (
    <LayerTrigger layer={tooltip} placement="top" alignment="middle" key={recipientId}>
      <span
        className="ComposeMessageRecipientsInput-token"
        data-test-selector={`split-composer-token-${recipientId}`}>
        <MediaObject center gutter="xsmall">
          {avatar}
          {selectedRoles && selectedRoles.length ? (
            <React.Fragment>
              <Text className="ComposeMessageRecipientsInput-token-name" size="xsmall">
                {displayName}
              </Text>
              <Glyph name="chevron-right" color="graphite" size="xxsmall" />
              <Text
                className="ComposeMessageRecipientsInput-token-roles"
                data-test="selected-roles"
                size="xsmall"
                truncate>
                {selectedRolesText}
              </Text>
            </React.Fragment>
          ) : (
            <Text className="ComposeMessageRecipientsInput-token-text" size="xsmall">
              {displayName}
            </Text>
          )}
        </MediaObject>
      </span>
    </LayerTrigger>
  );
}

export default Token;
